/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  ROLES,
  UserRoles,
  UserSubRoles,
  isUserLicensed,
  USER_LICENSE_TYPE_LABEL_MAP,
  UserLicenseType,
  userRolesToLabelMap,
} from '@bb/users';
import { StyledAlert } from '@brokerbay/design-components';
import {
  buildFullName,
  SUPPORT_EMAIL,
  featureEnabled,
  capitalize,
  isValidUrl,
} from 'utils';
import { Feature } from '@bb/automaton';

import { save as saveNewTeam } from 'api/TeamController';

import { InputFile } from 'filestack-js';
import fileStackClient from '@app/filestack';
import {
  PickerResponse,
  PickerFileMetadata,
} from 'filestack-js/build/main/lib/picker';
import {
  Title,
  Text,
  Icon,
  Form,
  InputNew as AntdInput,
  Input,
  Row,
  Col,
  Select,
  SelectOption,
  Radio,
  Switch,
  Checkbox,
  Spin,
  Popconfirm,
  message,
  Tooltip,
  DescriptionText,
  theme,
  PronounTags,
  PRONOUN_TAGS,
  InputTextArea,
} from 'components';
import { AuthContext, ListingLabelContext } from 'contexts';
import errorReporter from '@app/errorReporter';
import { AuthController } from 'api';
import { Pronoun, UserFeatures } from '@bb/users/dist/types/enums';

import {
  SupraSystemSubscriptionStatus,
  SupraSystemSubscriptionType,
} from '@bb/domain/dist/types/SupraSystemSubscription';
import getActivationFeePrerequisites, {
  ActivationFeePrerequisites,
} from 'api/SupraController/getActivationFeePrerequisites';
import { checkAutomaticActivationFeeWaive } from 'api/SupraSystemSubscriptionController';
import type { IGetOrgMemberNumberStateResponse } from '@/app-package';
import FormContext, { FormContextType } from './FormContext';
import { Divider, LinkButton } from './components';
import UploadAvatarButton from './ProfileTab/UploadAvatarButton';
import {
  ALT_PHONE_TYPES,
  ERROR_MESSAGES,
  NO_BOARD,
  REQUEST_TRANSFER_TEXT,
} from './constants';
import { cantRequestTransfer, shouldRequestTransfer } from './util';
import MemberNumberMultiMatch from './ProfileTab/MemberNumberMultiMatch';
import AddNewTeamModal from './AddNewTeamModal';
import { UserState } from './types';

const getMemberNumberHelpText = (
  valid: FormContextType['memberNumbersValid'][number],
  boardDirty: boolean | undefined,
  numberDirty: boolean | undefined,
  mlsUniqueIdDirty: boolean | undefined,
) => {
  const { board, number, mlsUniqueId } = valid;
  if (numberDirty && !number) return ERROR_MESSAGES.mlsNumber;
  if (boardDirty && !board) return ERROR_MESSAGES.mlsBoard;
  if (mlsUniqueIdDirty && !mlsUniqueId) {
    return ERROR_MESSAGES.mlsUniqueId;
  }
  return undefined;
};

const getMemberNumberValidateStatus = (
  valid: FormContextType['memberNumbersValid'][number],
  boardDirty: boolean | undefined,
  numberDirty: boolean | undefined,
  mlsUniqueIdDirty: boolean | undefined,
): 'error' | undefined => {
  const { board, number, mlsUniqueId } = valid;
  if ((boardDirty && !board) || (numberDirty && !number)) return 'error';
  if (mlsUniqueIdDirty && !mlsUniqueId) {
    return 'error';
  }
  return undefined;
};

const buildBannerMessage = ({
  firstName = '',
  lastName = '',
  primaryNumber = '',
  primaryMemberNumberState,
  linkDisabled,
  requestTransfer,
}: {
  firstName: string;
  lastName: string;
  primaryNumber: string;
  primaryMemberNumberState?: IGetOrgMemberNumberStateResponse;
  linkDisabled: boolean;
  requestTransfer: () => Promise<void>;
}) => (
  <>
    <Text>
      <Text strong>{`${buildFullName(
        firstName,
        lastName,
      )} (${primaryNumber})`}</Text>
      {` already belongs to `}
      <Text strong>{`${primaryMemberNumberState?.org?.name || ''}.`}</Text>
      {` Would you like
    to transfer them to your brokerage?`}
    </Text>
    <Popconfirm
      title={REQUEST_TRANSFER_TEXT}
      okText="Yes"
      cancelText="No"
      onConfirm={requestTransfer}
      disabled={linkDisabled}
      width={336}
      placement="bottomRight"
    >
      <LinkButton disabled={linkDisabled}>Request Transfer</LinkButton>
    </Popconfirm>
  </>
);
//
// Component
//

const ProfileTab = ({
  requestTransfer,
}: {
  requestTransfer: () => Promise<void>;
}) => {
  const {
    auth,
    onboardingPortalConfig,
    isBrokerBay,
    hasFeature,
    orgSupraSubscriptionOptions,
  } = useContext(AuthContext);

  const { getBoardName: formatBoardName, getMemberNumberString } =
    useContext(ListingLabelContext);
  /* The giant data structure to hold all of the state data about the new user that is being created. This is NOT data about the current user performing the interactions. */
  const {
    isProfile,

    boards,
    boardMap,
    teams,
    setTeams,
    locations,
    loading,

    userState,
    setUserState,

    userStateDirty,
    setUserStateDirty,

    memberNumberStateLoading,
    setMemberNumberStateLoading,

    firstNameValid,
    lastNameValid,
    memberNumbersValid,
    memberNumbersExist,
    mobileValid,
    altPhoneTypeValid,
    altPhoneNumberValid,
    emailValidity,
    branchValid,
    addressValid,
    branchAccessValid,
    teamAccessValid,
    passwordValid,
    memberNumberStates,
    primaryMemberNumberState,
    fetchedPrimaryNumberString,
    primaryNumber,
    memberNumbersAllValid,
    licenseTypeValid,
    fetchedUserState,
    userSubRoleValid,
    waiveActivationFeeReasonValid,
    supraSubscriptionTypeValid,
    supraSubscriptionBillingValid,

    isNew,
    isAnsweringOrMlsStaff,
    isDeleted,
    isIncomingTransfer,
    setWorking,
    mapBoards,
  } = useContext(FormContext);

  const {
    avatarFilestackId,
    avatarFullFilestackId,
    firstName,
    lastName,
    role,
    title,
    licenseType,
    memberNumbers,
    mobile,
    officeExtension,
    pronoun,
    altPhone,
    altPhoneType,
    email,
    branch,
    address,
    branchAccess,
    teamAccess,
    password,
    disabled,
    isAgentChatHidden,
    isChatAdmin,
    isChatInactive,
    notes,
  } = userState;

  // We're using the "dirty" variables to track whether a field has been interacted with or not by the user
  const {
    firstName: firstNameDirty,
    lastName: lastNameDirty,
    mobile: mobileDirty,
    altPhone: altPhoneDirty,
    email: emailDirty,
    branch: branchDirty,
    team: teamDirty,
    address: addressDirty,
    password: passwordDirty,
    licenseType: licenseTypeDirty,
    supraSubscription: {
      waiveActivationFeeReason: waiveActivationFeeReasonDirty,
    },
  } = userStateDirty;

  /* Keep track of whether:
   * we want the password they're setting for the new user to be visible or not
   * they've chosen to waive the activation fee for the new user or not
   * the reason they're waiving the activation fee
   * whether they are allowed to waive the activation fee & their contract
   * the amount to pay for the activation fee
   * whether activation fee should be automatically waived
   */
  const [passwordVisible, setPasswordVisible] = React.useState(false);
  const [waiveActivationFee, setWaiveActivationFee] = React.useState(false);
  const [waiveFeeReason, setWaiveFeeReason] = React.useState('');
  const [activationFeePrereqs, setActivationFeePrereqs] =
    useState<ActivationFeePrerequisites | null>(null);
  const [activationFeeRate, setActivationFeeRate] = useState('');
  const [
    isActivationFeeAutomaticallyWaived,
    setIsActivationFeeAutomaticallyWaived,
  ] = useState<boolean>(false);

  useEffect(() => {
    /** Hard-coded call to fetch whether we can waive activation fees for this user */
    const fetchActivationFeePrereqs = async () => {
      if (
        !featureEnabled('supraBillingApiFeature') ||
        !featureEnabled('keyholderBillingFeature')
      ) {
        return;
      }

      const result = await getActivationFeePrerequisites({
        orgId: auth.org.id,
        orgType: 'brokerage',
      });

      setActivationFeePrereqs(result);

      // Look for the item where contractRateLabel === 'ACC-ACTIVATION' or 'ACC-Activation'
      const activationContract = result.activeContracts?.find(
        c => c.contractRateLabel.toUpperCase() === 'ACC-ACTIVATION',
      );

      if (!activationContract) return;
      if (activationContract.rate) {
        setActivationFeeRate(activationContract.rate);
      } else {
        message.error('Activation Rate not found.');
      }
    };

    fetchActivationFeePrereqs();
  }, [auth.org.id]);

  useEffect(() => {
    /** Hard-coded check to see whether the user being edited
     * was previously subscribed or had legacy eKey data.
     */
    const checkForExistingSubscriptions = async () => {
      if (
        !featureEnabled('supraBillingApiFeature') ||
        !featureEnabled('keyholderBillingFeature') ||
        !userState.id
      ) {
        return;
      }

      if (auth.isAdmin) {
        const automaticallyWaiveActivationFee =
          await checkAutomaticActivationFeeWaive({ userId: userState.id });

        // A user who has never had a Supra One subscription before, but had
        // legacy eKey data should always have their Activation Fee waived.
        if (automaticallyWaiveActivationFee) {
          setIsActivationFeeAutomaticallyWaived(true);
          setWaiveFeeReason(
            'Starting brand-new Supra One subscription for user with legacy eKey data',
          );
        }
      }
    };

    checkForExistingSubscriptions();
  }, [auth.isAdmin, userState.id]);

  useEffect(() => {
    // Set the waiving any time isActivationFeeAutomaticallyWaived is changed
    setWaiveActivationFee(isActivationFeeAutomaticallyWaived);
  }, [isActivationFeeAutomaticallyWaived]);

  //
  // Handlers
  //

  const [avatarFilename, setAvatarFilename] = useState<string>();

  const getBoardName = (board: string | 'No Board', fullName: string) => {
    const boardName = formatBoardName(board, false);
    return (
      <StyledBoardName>
        <Text>{boardName}</Text>
        <Text type="secondary">{fullName}</Text>
      </StyledBoardName>
    );
  };

  const shouldDisplayDeactivateUserLogin = useMemo(
    () => !auth?.org?.features.includes('SUPRA_MVP') || isBrokerBay,
    [auth, isBrokerBay],
  );

  const shouldDisplayPassword = useMemo(() => {
    if (auth.clientIdpFeatureEnabled) return false;
    if (
      auth.isBrokerBay ||
      auth.org.type === 'mlsStaff' ||
      auth.parentOrgType === 'mlsStaff' ||
      isProfile
    )
      return true;

    if (auth.org.country === 'USA' && isNew) return false;

    return true;
  }, [auth, isNew, isProfile]);

  const showShowPasswordCheckboxAndInput = () => {
    if (
      auth.isBrokerBay ||
      auth.org.type === 'mlsStaff' ||
      auth.parentOrgType === 'mlsStaff' ||
      isProfile
    )
      return true;
    if (auth.org.country === 'USA') return false;
    return true;
  };

  const handleAddMemberNumber = () => {
    setUserState(cur => ({
      ...cur,
      memberNumbers: [...cur.memberNumbers, {}],
    }));
  };

  const [addTeamModalVisible, setAddTeamModalVisible] = useState(false);
  const toggleAddTeamModal = () => setAddTeamModalVisible(!addTeamModalVisible);
  const handleNewTeamSave = async (payload: {
    name: string;
    email: string;
    phone: string;
  }) => {
    try {
      const { data: newTeam } = await saveNewTeam(payload);
      setTeams([newTeam, ...(teams || [])]);
      setUserState(cur => ({
        ...cur,
        teamAccess: [newTeam._id, ...(teamAccess || [])],
      }));
    } catch (err) {
      errorReporter.error(err);
      message.error('Unable to create new team');
    } finally {
      toggleAddTeamModal();
    }
  };

  const supraSubscriptionType = useMemo(() => {
    if (featureEnabled('supraBillingApiFeature')) {
      const { keyTypes: orgKeyTypeOptions } = orgSupraSubscriptionOptions || {};
      return orgKeyTypeOptions || [];
    }

    return [
      SupraSystemSubscriptionType.FULL,
      SupraSystemSubscriptionType.LIMITED,
      SupraSystemSubscriptionType.MANAGED_ACCESS,
    ];
  }, [orgSupraSubscriptionOptions]);

  const supraSubKeyTypes = useMemo(() => {
    const { supraSubscription } = userState;
    if (!supraSubscription) return [];

    const { type } = supraSubscription;

    if (featureEnabled('supraBillingApiFeature')) {
      const { limitedSubRoles, managedAccessSubRoles } =
        orgSupraSubscriptionOptions || {};

      if (type === SupraSystemSubscriptionType.LIMITED) {
        return limitedSubRoles;
      }
      if (type === SupraSystemSubscriptionType.MANAGED_ACCESS) {
        return managedAccessSubRoles;
      }
      return [];
    }

    if (type === SupraSystemSubscriptionType.LIMITED) {
      return [
        UserSubRoles.Affiliate,
        UserSubRoles.Training,
        UserSubRoles['SupraNET Manual Coop'],
      ];
    }
    if (type === SupraSystemSubscriptionType.MANAGED_ACCESS) {
      return [
        UserSubRoles['Non-Member Agent'],
        UserSubRoles['Inspector/Appraiser'],
        UserSubRoles['Pest Control'],
        UserSubRoles['Photographer/Stager'],
        UserSubRoles.Tradesperson,
        UserSubRoles.Other,
      ];
    }
    return [];
  }, [userState, orgSupraSubscriptionOptions]);

  const supraSubscriptionBillingTypes = useMemo(() => {
    const { supraSubscription } = userState;
    if (!supraSubscription) return [];

    const { type } = supraSubscription;
    const { subRole } = userState;
    if (
      type &&
      [
        SupraSystemSubscriptionType.FULL,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ].includes(type)
    )
      return ['billable'];

    if (featureEnabled('supraBillingApiFeature')) {
      const { limitedOtherBilling } = orgSupraSubscriptionOptions || {};
      return limitedOtherBilling || [];
    }
    return ['billable', 'non-billable'];
  }, [userState, orgSupraSubscriptionOptions]);

  const keyTypeHelpMessage = useMemo(() => {
    if (!supraSubscriptionTypeValid && userStateDirty.supraSubscription.type) {
      return ERROR_MESSAGES.supraSubscriptionKeyTypeRequired;
    }
    return undefined;
  }, [userStateDirty, supraSubscriptionTypeValid]);

  const subRoleHelpMessage = useMemo(() => {
    if (!userSubRoleValid && userStateDirty.subRole) {
      return ERROR_MESSAGES.supraSubscriptionSubRoleRequired;
    }
    return undefined;
  }, [userSubRoleValid, userStateDirty]);

  const billingHelpMessage = useMemo(() => {
    if (
      !supraSubscriptionBillingValid &&
      userStateDirty.supraSubscription.billing &&
      userState.subRole !== UserSubRoles['SupraNET Manual Coop']
    ) {
      if (userState.supraSubscription?.billing !== '') {
        return ERROR_MESSAGES.maxNonBillableKeysReached;
      }
    }
    return undefined;
  }, [supraSubscriptionBillingValid, userStateDirty, userState]);

  const showWaiveActivationFee = useMemo(
    () =>
      activationFeePrereqs?.canWaiveActivationFee &&
      userState.supraSubscription?.billing === 'billable' &&
      (!userState.supraSubscription.status ||
        userState.supraSubscription.status ===
          SupraSystemSubscriptionStatus.unsubscribed),
    [activationFeePrereqs, userState],
  );

  const isKeyTypeDisabled: boolean =
    isDeleted ||
    userState.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.suspended ||
    userState.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.deactivated;

  const isSubRoleDisabled: boolean =
    isDeleted ||
    userState.supraSubscription?.type === SupraSystemSubscriptionType.FULL ||
    userState.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.suspended ||
    userState.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.deactivated;

  const areLimitedTogglesDisabled: boolean =
    userState.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.suspended ||
    userState.supraSubscription?.status ===
      SupraSystemSubscriptionStatus.deactivated;

  const handleKeyTypeChange = (type: SupraSystemSubscriptionType) => {
    if (
      [
        SupraSystemSubscriptionType.FULL,
        SupraSystemSubscriptionType.MANAGED_ACCESS,
      ].includes(type)
    ) {
      setUserState(cur => ({
        ...cur,
        subRole: '',
        supraSubscription: {
          ...cur.supraSubscription,
          type,
          billing: 'billable',
        },
      }));
    } else {
      setUserState(cur => ({
        ...cur,
        subRole: '',
        supraSubscription: { ...cur.supraSubscription, type, billing: '' },
      }));
    }
    setUserStateDirty(cur => ({
      ...cur,
      supraSubscription: {
        ...cur.supraSubscription,
        type: true,
        subRole: true,
        billing: true,
      },
    }));
  };

  const handleSubKeyTypeChange = (subRole: UserSubRoles) => {
    if (subRole === UserSubRoles.Affiliate) {
      setUserState(cur => ({
        ...cur,
        subRole,
        features: cur.features?.filter(
          f => f !== UserFeatures['Scheduling Services'],
        ),
        supraSubscription: {
          ...cur.supraSubscription,
          subRole,
          billing: 'billable',
          assignLockboxesReleaseShackles: false,
        },
      }));
    } else if (
      subRole === UserSubRoles.Training ||
      subRole === UserSubRoles['SupraNET Manual Coop']
    ) {
      setUserState(cur => ({
        ...cur,
        subRole,
        features: cur.features?.includes(UserFeatures['Scheduling Services'])
          ? cur.features
          : cur.features
            ? [...cur.features, UserFeatures['Scheduling Services']]
            : [UserFeatures['Scheduling Services']],
        supraSubscription: {
          ...cur.supraSubscription,
          billing: 'non-billable',
          assignLockboxesReleaseShackles: true,
        },
      }));
    } else {
      let billing: Required<UserState>['supraSubscription']['billing'] = '';
      if (
        userState.supraSubscription?.type ===
        SupraSystemSubscriptionType.MANAGED_ACCESS
      ) {
        billing = 'billable';
      } else {
        billing =
          orgSupraSubscriptionOptions?.limitedOtherBilling?.length === 1
            ? orgSupraSubscriptionOptions.limitedOtherBilling[0]
            : '';
      }

      setUserState(cur => ({
        ...cur,
        subRole,
        features: cur.features?.filter(
          f => f !== UserFeatures['Scheduling Services'],
        ),
        supraSubscription: {
          ...cur.supraSubscription,
          billing,
          assignLockboxesReleaseShackles: false,
        },
      }));
    }
    setUserStateDirty(cur => ({
      ...cur,
      supraSubscription: {
        ...cur.supraSubscription,
        type: true,
        subRole: true,
        billing: true,
      },
    }));
  };

  const handleSchedulingServiceToggle = (checked: boolean) => {
    if (
      checked &&
      !userState.features?.includes(UserFeatures['Scheduling Services'])
    ) {
      setUserState(cur => ({
        ...cur,
        features: [
          ...(cur.features || []),
          UserFeatures['Scheduling Services'],
        ],
      }));
    }

    if (
      !checked &&
      userState.features?.includes(UserFeatures['Scheduling Services'])
    ) {
      setUserState(cur => ({
        ...cur,
        features: cur.features?.filter(
          f => f !== UserFeatures['Scheduling Services'],
        ),
      }));
    }
  };

  const handleOnPronounChange = (
    options: { value: string; label: string }[],
  ) => {
    const pronounArray = options.map(({ value }) => value);
    setUserState(cur => ({ ...cur, pronoun: pronounArray as Pronoun[] }));
  };

  const handleRemoveMemberNumber = (idx: number) => {
    setUserState(cur => {
      const numbers = [...cur.memberNumbers];
      numbers.splice(idx, 1);
      return { ...cur, memberNumbers: numbers };
    });
  };

  const handleSelectPrimaryBoard = (idx: number) => {
    setUserState(cur => ({
      ...cur,
      memberNumbers: cur.memberNumbers.map((number, mapIdx) => ({
        ...number,
        isPrimary: mapIdx === idx,
      })),
    }));
  };

  const handleWaiveActivationFee = (isChecked: boolean) => {
    // Re-render the checkbox section
    setWaiveActivationFee(isChecked);

    // Clear the reason when unchecking
    if (!isChecked) {
      setWaiveFeeReason('');
    }

    // Save a boolean indicating the user has interacted with the field
    setUserStateDirty(cur => ({
      ...cur,
      supraSubscription: {
        ...cur.supraSubscription,
        waiveActivationFeeReason: true,
      },
    }));
    // Save the data to the user that regards waiving the fee
    const reason = isChecked ? waiveFeeReason : '';
    setUserState(cur => ({
      ...cur,
      supraSubscription: {
        ...cur.supraSubscription,
        waiveActivationFee: isChecked,
        waiveActivationFeeReason: reason,
      },
    }));
  };

  const showAvatarPicker = () => {
    // Keep uncropped avatar in scope here for when upload is done
    let avatarFull: PickerFileMetadata;

    const handleFileSelected = async (file: PickerFileMetadata) => {
      // store the full avatar for later (handleFileUploaded)
      if (file.source === 'url') {
        if (!isValidUrl(file.originalPath))
          throw new Error('Invalid URL format. Please try again.');
        avatarFull = (await fileStackClient.storeURL(
          file.originalPath,
        )) as PickerFileMetadata;
      } else {
        avatarFull = await fileStackClient.upload(
          file.originalFile as InputFile,
          undefined,
          { altText: 'user avatar' },
        );
      }

      setAvatarFilename(file.filename);
    };

    const handleFileUploaded = async (results: PickerResponse) => {
      setUserState(cur => ({
        ...cur,
        avatarFilestackId: results.filesUploaded[0].handle,
        avatarFullFilestackId: avatarFull.handle,
      }));
    };

    const opts = {
      accept: 'image/*',
      maxSize: 1024 * 1024 * 2,
      uploadInBackground: false,
      exposeOriginalFile: true,
      fromSources: ['local_file_system', 'url'],
      transformations: { rotate: true, crop: { aspectRatio: 1, force: true } },
      onFileSelected: handleFileSelected,
      onUploadDone: handleFileUploaded,
    };

    const picker = fileStackClient.picker(opts);
    picker.open();
  };

  const showEditAvatarPicker = () => {
    if (!avatarFilestackId) return;

    const handleFileUploaded = async (results: PickerResponse) => {
      setUserState(cur => ({
        ...cur,
        avatarFilestackId: results.filesUploaded[0].handle,
      }));
    };

    const opts = {
      accept: 'image/*',
      maxSize: 1024 * 1024 * 2,
      uploadInBackground: false,
      exposeOriginalFile: true,
      transformations: { rotate: true, crop: { aspectRatio: 1, force: true } },
      onUploadDone: handleFileUploaded,
    };

    const picker = fileStackClient.picker(opts);
    picker.crop([`https://cdn.filestackcontent.com/${avatarFullFilestackId}`]);
  };

  const handleDeleteAvatar = () => {
    setUserState(cur => ({
      ...cur,
      avatarFilestackId: undefined,
      avatarFullFilestackId: undefined,
    }));
  };

  const getMemberNumberSuffix = (
    board?: string,
    number?: string,
    isPrimary?: boolean,
  ) => {
    if (licenseType !== UserLicenseType.LICENSED) return null;

    if (board === NO_BOARD) {
      return (
        <Tooltip
          title={`This agent's license number ${
            number?.toUpperCase() || ''
          } cannot be linked to your board data feeds. ${
            isPrimary
              ? 'The agent will have a limited experience on the platform until the discrepancy is resolved. '
              : ''
          }Contact ${SUPPORT_EMAIL} if you have any questions.`}
        >
          <div>
            <Icon type="antdWarningFilled" color="warningColor" />
          </div>
        </Tooltip>
      );
    }
    if (!board || !number) {
      // Empty fields; return empty span because undefined will recreate the dom structure
      // https://ant.design/components/input/#FAQ
      return <span />;
    }
    const key = getMemberNumberString(board, number);
    const memberNumberState = memberNumberStates[key];
    if (!memberNumberState) {
      // Still fetching; return empty span because undefined will recreate the dom structure
      // https://ant.design/components/input/#FAQ
      return <span />;
    }
    if (memberNumberState.multiMatch === true) return <span />;

    if (!memberNumberState.inRoster || !memberNumberState.inRosterOrg) {
      return (
        <Tooltip
          title={`This agent's member ID (${getMemberNumberString(
            board,
            number,
          )}) does not appear in your board data feeds. ${
            isPrimary
              ? 'The agent will have a limited experience on the platform until the discrepancy is resolved. '
              : ''
          }Contact ${SUPPORT_EMAIL} if you have any questions.`}
        >
          <div>
            <Icon type="antdWarningFilled" color="warningColor" />
          </div>
        </Tooltip>
      );
    }
    return (
      <Tooltip title="This agent appears in your data feed.">
        <div>
          <Icon type="checkCircle" color="successColor" />
        </div>
      </Tooltip>
    );
  };

  const handlePasswordReset = async () => {
    if (!fetchedUserState?.email) {
      message.error('Invalid email');
      return;
    }
    try {
      await AuthController.resetPassword(fetchedUserState.email);
      message.success('Succesfully sent password reset link');
    } catch (err) {
      errorReporter.error(err);
      message.error('Unable to send password link.');
    }
  };

  const allowedUserRoles = Object.entries(userRolesToLabelMap).filter(
    ([key]) => {
      const userRole = ROLES[key as UserRoles];
      if (ROLES.OWNER === userRole) {
        return false;
      }

      if (ROLES.NON_MEMBER === userRole) {
        return auth.parentOrgType === 'mlsStaff';
      }

      if (ROLES.TEAM_ADMIN === userRole) {
        return hasFeature(Feature.TEAMS);
      }
      if (ROLES.MANAGER === userRole) {
        return ROLES[auth.role] >= ROLES.BRANCH_ADMIN;
      }
      return ROLES[auth.role] >= ROLES[key as UserRoles];
    },
  );

  const handleUserRoleChange = (val: UserRoles) => {
    /* Every time the role is changed, uncheck the waiveActivationFee box so that
       we can force the user to trigger the validation on the Reason texbox again
    */
    if (userState.supraSubscription) {
      setWaiveActivationFee(false);
      userState.supraSubscription!.waiveActivationFee = false;
    }

    setUserState(curr => ({
      ...curr,
      role: val,
      subRole: '',
      supraSubscription: { ...curr.supraSubscription, type: '', billing: '' },
    }));
  };

  const pronounTags = pronoun?.map(key => PRONOUN_TAGS[key as Pronoun]);

  //
  // Render
  //

  if (loading) return <Spin size="large" />;

  return (
    <Form layout="vertical">
      <StyledTitle level={3}>Details</StyledTitle>
      <Form.Item label="Profile Photo">
        <UploadAvatarButton
          data-cy="user-upload-profile-photo"
          avatarFilestackId={avatarFilestackId}
          filename={avatarFilename}
          onClick={showAvatarPicker}
          onEditClick={showEditAvatarPicker}
          onDeleteClick={handleDeleteAvatar}
        />
      </Form.Item>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            label="First Name"
            validateStatus={
              firstNameDirty && !firstNameValid ? 'error' : undefined
            }
            help={
              firstNameDirty && !firstNameValid
                ? ERROR_MESSAGES.firstName
                : undefined
            }
          >
            <AntdInput
              data-cy="user-input-first-name"
              placeholder="First Name"
              disabled={isDeleted}
              value={firstName}
              onChange={({ target: { value } }) =>
                setUserState(cur => ({ ...cur, firstName: value }))
              }
              onBlur={() =>
                setUserStateDirty(cur => ({ ...cur, firstName: true }))
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Last Name"
            validateStatus={
              lastNameDirty && !lastNameValid ? 'error' : undefined
            }
            help={
              lastNameDirty && !lastNameValid
                ? ERROR_MESSAGES.lastName
                : undefined
            }
          >
            <AntdInput
              data-cy="user-input-last-name"
              placeholder="Last Name"
              disabled={isDeleted}
              value={lastName}
              onChange={({ target: { value } }) =>
                setUserState(cur => ({ ...cur, lastName: value }))
              }
              onBlur={() =>
                setUserStateDirty(cur => ({ ...cur, lastName: true }))
              }
            />
          </Form.Item>
        </Col>
        {!isAnsweringOrMlsStaff && (
          <Col>
            <Form.Item label="Pronoun" data-cy="edit-profile-add-pronoun">
              <PronounTags
                onChange={handleOnPronounChange}
                tags={pronounTags}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      {!isAnsweringOrMlsStaff && !isProfile && (
        <>
          <Form.Item label="Role">
            <Select
              data-cy="user-select-role"
              value={role}
              onChange={handleUserRoleChange}
              disabled={isDeleted}
            >
              {allowedUserRoles.map(([key, label]) => (
                <SelectOption value={key} key={key}>
                  {label}
                </SelectOption>
              ))}
            </Select>
          </Form.Item>
          {role !== UserRoles.Agent && (
            <Form.Item
              label="License Type"
              validateStatus={
                licenseTypeDirty && !licenseTypeValid ? 'error' : undefined
              }
              help={
                licenseTypeDirty && !licenseTypeValid
                  ? ERROR_MESSAGES.licenseType
                  : undefined
              }
            >
              <Select
                data-cy="user-select-license-type"
                value={licenseType}
                onChange={val =>
                  setUserState(cur => ({ ...cur, licenseType: val }))
                }
                disabled={isDeleted}
              >
                {Object.entries(USER_LICENSE_TYPE_LABEL_MAP).map(
                  ([type, label]) => (
                    <SelectOption value={type} key={type}>
                      {label}
                    </SelectOption>
                  ),
                )}
              </Select>
            </Form.Item>
          )}
        </>
      )}
      {isUserLicensed({ licenseType }) && !isDeleted && !isProfile && (
        <Form.Item label="Title">
          <Select
            data-cy="user-select-title"
            value={title}
            onChange={val => setUserState(cur => ({ ...cur, title: val }))}
          >
            {Object.values(onboardingPortalConfig.titles).map(
              ({ label, value }) => (
                <SelectOption value={value} key={value}>
                  {label}
                </SelectOption>
              ),
            )}
          </Select>
        </Form.Item>
      )}
      {!isDeleted &&
        licenseType &&
        [
          UserLicenseType.LICENSED,
          UserLicenseType.UNLICENSED_WITH_MEMBER_NUMBER,
        ].includes(licenseType) &&
        !isProfile && (
          <>
            {primaryMemberNumberState?.multiMatch !== true &&
              primaryMemberNumberState?.inBrokerBay &&
              !primaryMemberNumberState.inOrg &&
              primaryMemberNumberState.isPendingTransfer &&
              primaryNumber &&
              firstName &&
              firstNameValid &&
              lastName &&
              lastNameValid &&
              isNew && (
                <>
                  <StyledAlert
                    message={
                      <Text>
                        <Text strong>{`${buildFullName(firstName, lastName)} (${
                          primaryNumber.number
                        })`}</Text>{' '}
                        is currently pending transfer to another brokerage. You
                        cannot submit this request at this time. For assistance
                        please contact {SUPPORT_EMAIL}
                      </Text>
                    }
                    icon={
                      <Icon
                        type="antdInfoCircleFilled"
                        color="warning"
                        width={14}
                      />
                    }
                    type="warning"
                    showIcon
                  />
                  <br />
                </>
              )}
            {primaryMemberNumberState?.multiMatch !== true &&
              shouldRequestTransfer({
                primaryMemberNumberState,
                primaryNumber,
                firstNameValid,
                isNew,
                lastNameValid,
                licenseType,
              }) && (
                <>
                  <StyledAlert
                    message={buildBannerMessage({
                      firstName: firstName || '',
                      lastName: lastName || '',
                      primaryNumber: primaryNumber?.number || '',
                      primaryMemberNumberState,
                      linkDisabled: Boolean(
                        cantRequestTransfer({
                          branchValid,
                          addressValid,
                          memberNumbersAllValid,
                          emailValid: emailValidity.isValid,
                          mobileValid,
                          memberNumberStateLoading,
                        }),
                      ),
                      requestTransfer,
                    })}
                    icon={
                      <Icon
                        type="antdInfoCircleFilled"
                        color="primary"
                        width={14}
                      />
                    }
                    showIcon
                  />
                  <br />
                </>
              )}
            {primaryNumber &&
              primaryMemberNumberState &&
              primaryMemberNumberState.multiMatch !== true &&
              (!primaryMemberNumberState.inRoster ||
                !primaryMemberNumberState.inRosterOrg) &&
              isUserLicensed({ licenseType }) && (
                <MemberNumberAlert
                  message={
                    <Text>
                      This agent&apos;s member ID{' '}
                      {fetchedPrimaryNumberString &&
                        ` (${fetchedPrimaryNumberString})`}{' '}
                      does not appear in your data feeds. The agent will have a
                      limited experience on the platform until the discrepancy
                      is resolved. Contact{' '}
                      <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> if
                      you have any questions.
                    </Text>
                  }
                  icon={
                    <Icon
                      type="antdExclamationCircleFilled"
                      color="warning"
                      width={14}
                    />
                  }
                  type="warning"
                  showIcon
                />
              )}
            <FormItemLabelContainer>
              <Text>Member Numbers</Text>
            </FormItemLabelContainer>
            {memberNumbers.map((memberNumber, idx) => (
              <MemberNumbersFormItem
                key={idx}
                validateStatus={getMemberNumberValidateStatus(
                  memberNumbersValid[idx],
                  memberNumber.boardDirty,
                  memberNumber.numberDirty,
                  memberNumber.mlsUniqueIdDirty,
                )}
                help={getMemberNumberHelpText(
                  memberNumbersValid[idx],
                  memberNumber.boardDirty,
                  memberNumber.numberDirty,
                  memberNumber.mlsUniqueIdDirty,
                )}
              >
                <AntdInput.Group compact>
                  <InputGroupSelect
                    data-cy="user-select-member-board"
                    dropdownMatchSelectWidth={false}
                    showSearch
                    placeholder="Select"
                    value={mapBoards(memberNumber.board)}
                    onChange={boardId => {
                      const numbers = [...memberNumbers];
                      if (boardId === 'NO_BOARD') {
                        numbers[idx].board = 'NO_BOARD';
                        numbers[idx].boardId = undefined;
                      } else {
                        numbers[idx].board = boardMap?.[boardId].board;
                        numbers[idx].boardId = boardId;
                      }
                      numbers[idx].boardDirty = true;
                      delete numbers[idx].mlsUniqueId;
                      setMemberNumberStateLoading(true);
                      setUserState(cur => ({ ...cur, memberNumbers: numbers }));
                    }}
                    onBlur={() =>
                      setUserState(cur => {
                        const next = { ...cur };
                        next.memberNumbers[idx].boardDirty = true;
                        setMemberNumberStateLoading(false);
                        return next;
                      })
                    }
                    filterOption={(input: string, option) =>
                      formatBoardName(
                        boardMap?.[option?.value ?? '']?.board.toUpperCase() ||
                          NO_BOARD,
                      ).includes(input.toUpperCase())
                    }
                    $width="25%"
                  >
                    {boards?.map(({ _id, board, name }) => (
                      <SelectOption value={_id} key={_id}>
                        {getBoardName(board, name)}
                      </SelectOption>
                    ))}
                    {memberNumbers.length === 1 && (
                      <SelectOption value={NO_BOARD} key={NO_BOARD}>
                        {getBoardName(
                          'No Board',
                          'Not registered with a Real Estate Board',
                        )}
                      </SelectOption>
                    )}
                  </InputGroupSelect>
                  <InputGroupInput
                    data-cy="user-input-mlsnumber"
                    placeholder={
                      memberNumber.board === NO_BOARD
                        ? 'Provincial or State License Number (i.e. RECO)'
                        : 'MLS Member ID #'
                    }
                    suffix={getMemberNumberSuffix(
                      memberNumber.board,
                      memberNumber.number,
                      memberNumber.isPrimary,
                    )}
                    value={memberNumber.number}
                    onChange={({ target: { value } }) => {
                      const numbers = [...memberNumbers];
                      numbers[idx].number = value;
                      delete numbers[idx].mlsUniqueId;
                      setMemberNumberStateLoading(true);
                      setUserState(cur => ({ ...cur, memberNumbers: numbers }));
                    }}
                    onBlur={() =>
                      setUserState(cur => {
                        const next = { ...cur };
                        next.memberNumbers[idx].numberDirty = true;
                        next.memberNumbers[idx].number =
                          next.memberNumbers[idx].number?.trim();
                        setMemberNumberStateLoading(false);
                        return next;
                      })
                    }
                    $width="75%"
                  />
                </AntdInput.Group>
                <PrimaryMemberNumberRadio
                  checked={memberNumber.isPrimary}
                  onChange={() => handleSelectPrimaryBoard(idx)}
                >
                  Primary MLS / Association
                </PrimaryMemberNumberRadio>
                {memberNumbers.length > 1 && !memberNumber.isPrimary && (
                  <RemoveMemberNumberButton
                    onClick={() => handleRemoveMemberNumber(idx)}
                  >
                    <Icon
                      data-cy="user-radiobutton-memberId-remove"
                      type="antdMinusCircleOutlined"
                      color="danger"
                      width={14}
                      height={14}
                    />
                    Remove
                  </RemoveMemberNumberButton>
                )}
                <MemberNumberMultiMatch
                  memberNumber={memberNumber}
                  numberIndex={idx}
                />
              </MemberNumbersFormItem>
            ))}
            {memberNumbers[0]?.board !== NO_BOARD && (
              <Form.Item
                help={
                  !memberNumbersExist ? ERROR_MESSAGES.mlsNumbers : undefined
                }
              >
                <LinkButton
                  data-cy="user-button-add-member-number"
                  onClick={handleAddMemberNumber}
                >
                  <Icon
                    type="antdPlusCircleOutlined"
                    color="primary"
                    width={14}
                    height={14}
                  />
                  Add member number
                </LinkButton>
              </Form.Item>
            )}
          </>
        )}

      {auth.parentOrgType === 'mlsStaff' && hasFeature(Feature.SUPRA_MVP) && (
        <Form.Item label="Notes">
          <InputTextArea
            placeholder="Add Notes"
            data-cy="user-input-notes"
            value={notes}
            onChange={({ target: { value } }) =>
              setUserState(cur => ({ ...cur, notes: value }))
            }
          />
        </Form.Item>
      )}

      <Divider />

      <StyledTitle level={3}>Contact</StyledTitle>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item
            label="Mobile #"
            validateStatus={mobileDirty && !mobileValid ? 'error' : undefined}
            help={
              mobileDirty && !mobileValid ? ERROR_MESSAGES.mobile : undefined
            }
          >
            <StyledInput
              data-cy="user-input-mobile"
              phoneNumberMask
              disabled={isDeleted}
              value={mobile}
              onChange={({ target: { value } }) =>
                setUserState(cur => ({ ...cur, mobile: value }))
              }
              onBlur={() =>
                setUserStateDirty(cur => ({ ...cur, mobile: true }))
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Office Extension">
            <AntdInput
              data-cy="user-input-office-ext"
              placeholder="Ext"
              disabled={isDeleted}
              value={officeExtension}
              onChange={({ target: { value } }) =>
                setUserState(cur => ({ ...cur, officeExtension: value }))
              }
            />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label="Other Number"
        validateStatus={
          altPhoneDirty && (!altPhoneTypeValid || !altPhoneNumberValid)
            ? 'error'
            : undefined
        }
        help={
          altPhoneDirty &&
          (!altPhoneTypeValid
            ? ERROR_MESSAGES.altPhone
            : !altPhoneNumberValid
              ? ERROR_MESSAGES.mobile
              : undefined)
        }
      >
        <AntdInput.Group compact>
          <InputGroupSelect
            data-cy="user-select-otherNumber-type"
            defaultValue="Direct"
            disabled={isDeleted}
            value={altPhoneType}
            onChange={type =>
              setUserState(cur => ({ ...cur, altPhoneType: type }))
            }
            $width="25%"
          >
            {ALT_PHONE_TYPES.map(type => (
              <SelectOption value={type} key={type}>
                {type}
              </SelectOption>
            ))}
          </InputGroupSelect>
          <StyledInput
            data-cy="user-input-otherNumber"
            phoneNumberMask
            disabled={isDeleted}
            value={altPhone}
            onChange={({ target: { value } }) =>
              setUserState(cur => ({ ...cur, altPhone: value }))
            }
            onBlur={() =>
              setUserStateDirty(cur => ({ ...cur, altPhone: true }))
            }
            $width="75%"
          />
        </AntdInput.Group>
      </Form.Item>
      <Form.Item
        label="Email"
        validateStatus={
          emailDirty && !emailValidity.isValid ? 'error' : undefined
        }
        help={
          emailDirty && !emailValidity.isValid
            ? emailValidity.errorMessage
            : undefined
        }
      >
        <AntdInput
          data-cy="user-input-email"
          placeholder="Email"
          disabled={isDeleted}
          value={email}
          onChange={({ target: { value } }) => {
            setUserState(cur => ({ ...cur, email: value }));
            setWorking(true);
          }}
          onBlur={() => setUserStateDirty(cur => ({ ...cur, email: true }))}
        />
      </Form.Item>
      {shouldDisplayPassword && (
        <Form.Item
          label={isProfile || auth.isBrokerBay ? 'Reset Password' : 'Password'}
          validateStatus={passwordDirty && !passwordValid ? 'error' : undefined}
          help={
            passwordDirty && !passwordValid
              ? ERROR_MESSAGES.password
              : undefined
          }
          tooltip={ERROR_MESSAGES.password}
        >
          <AntdInput
            // Hiding input if it's a GA/BA/TA from USA
            hidden={!showShowPasswordCheckboxAndInput()}
            type={passwordVisible ? 'text' : 'password'}
            value={password}
            onChange={({ target: { value } }) =>
              setUserState(cur => ({ ...cur, password: value }))
            }
            onBlur={() =>
              setUserStateDirty(cur => ({ ...cur, password: true }))
            }
            title={ERROR_MESSAGES.password}
          />
          <PasswordControlContainer>
            {/* Hiding the checkbox if it's GA/BA/TA from USA */}
            {showShowPasswordCheckboxAndInput() && (
              <StyledCheckbox
                data-cy="user-checkbox-show-password"
                checked={passwordVisible}
                onChange={({ target: { checked } }) =>
                  setPasswordVisible(checked)
                }
              >
                Show Password
              </StyledCheckbox>
            )}
            {!isNew && !isProfile && !isIncomingTransfer && (
              <LinkButton
                data-cy="user-button-send-password-reset-link"
                onClick={handlePasswordReset}
              >
                Send password reset link
              </LinkButton>
            )}
          </PasswordControlContainer>
        </Form.Item>
      )}
      {/* A Subscription section to only show to MLS staff who have subscribed to Supra. */}
      {auth.parentOrgType === 'mlsStaff' &&
        hasFeature(Feature.SUPRA_MVP) &&
        (!featureEnabled('supraBillingApiFeature') ||
          orgSupraSubscriptionOptions) && (
          <>
            <Divider />
            <StyledTitle level={3}>
              Subscription
              {(userState.supraSubscription?.status ===
                SupraSystemSubscriptionStatus.suspended ||
                userState.supraSubscription?.status ===
                  SupraSystemSubscriptionStatus.deactivated) && (
                <Tooltip
                  title={`Unable to update subscription while current status is ${userState.supraSubscription?.status.toUpperCase()}.`}
                >
                  <Inline>
                    <Icon type="antdWarningFilled" color="warningColor" />
                  </Inline>
                </Tooltip>
              )}
            </StyledTitle>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label="Key Type"
                  validateStatus={
                    !supraSubscriptionTypeValid &&
                    userStateDirty.supraSubscription.type
                      ? 'error'
                      : undefined
                  }
                  help={keyTypeHelpMessage}
                >
                  <Select
                    data-cy="user-select-supra-keytype"
                    showSearch
                    placeholder="Select"
                    disabled={isKeyTypeDisabled}
                    optionFilterProp="children"
                    value={userState.supraSubscription?.type}
                    onChange={handleKeyTypeChange}
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onBlur={() =>
                      setUserStateDirty(cur => ({
                        ...cur,
                        supraSubscription: {
                          ...cur.supraSubscription,
                          type: true,
                          subRole: true,
                          billing: true,
                        },
                      }))
                    }
                    style={{ textTransform: 'capitalize' }}
                  >
                    {supraSubscriptionType.map(key => (
                      <SelectOption value={key} key={key}>
                        {capitalize(key.replaceAll('_', ' ').toLowerCase())}
                      </SelectOption>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Sub Role"
                  validateStatus={
                    !userSubRoleValid &&
                    userStateDirty.subRole &&
                    supraSubscriptionType.length !== 0
                      ? 'error'
                      : undefined
                  }
                  help={subRoleHelpMessage}
                >
                  <Select
                    data-cy="user-select-supra-sub"
                    showSearch
                    placeholder="Select"
                    disabled={isSubRoleDisabled}
                    optionFilterProp="children"
                    value={userState.subRole}
                    onChange={handleSubKeyTypeChange}
                    filterOption={(input, option) =>
                      (option?.children as unknown as string)
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onBlur={() =>
                      setUserStateDirty(cur => ({
                        ...cur,
                        type: true,
                        subRole: true,
                        billing: true,
                      }))
                    }
                    style={{ textTransform: 'capitalize' }}
                  >
                    {supraSubKeyTypes?.map(type => (
                      <SelectOption value={type} key={type}>
                        {capitalize(type.replaceAll('_', ' ').toLowerCase())}
                      </SelectOption>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={24}>
                <Title level={4}>Billing</Title>
                <Form.Item
                  validateStatus={
                    !supraSubscriptionBillingValid &&
                    userStateDirty.supraSubscription.billing &&
                    supraSubscriptionType.length !== 0
                      ? 'error'
                      : undefined
                  }
                  help={billingHelpMessage}
                >
                  {/* Billing select is always disabled for now. We can automatically deduce what this value will be based on the provided Key Type and Sub Role. */}
                  <Select
                    data-cy="user-select-supra-billing"
                    placeholder="Select"
                    disabled
                    value={userState.supraSubscription?.billing}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {supraSubscriptionBillingTypes.map(key => (
                      <SelectOption value={key} key={key}>
                        {capitalize(key)}
                      </SelectOption>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              {/* If applicable, display a checkbox & reason textbox that allows managers to waive the one-time activation fee for the acct */}
              {showWaiveActivationFee && (
                <Col span={24}>
                  <Form.Item>
                    <StyledCheckbox
                      data-cy=""
                      checked={waiveActivationFee}
                      disabled={
                        !activationFeePrereqs?.canWaiveActivationFee ||
                        isActivationFeeAutomaticallyWaived
                      }
                      onChange={({ target: { checked } }) =>
                        handleWaiveActivationFee(checked)
                      }
                    >
                      Waive Activation Fee
                    </StyledCheckbox>
                    <Tooltip
                      title={
                        isActivationFeeAutomaticallyWaived
                          ? `Legacy eKey users always have their activation fee waived on their first Supra One subscription.`
                          : `The agent will not need to pay the ${activationFeeRate} one-time activation fee for Supra services.`
                      }
                    >
                      <Inline>
                        <Icon type="antdInfoCircleOutlined" color="black" />
                      </Inline>
                    </Tooltip>
                  </Form.Item>
                  {
                    /* Only show reason form when checkbox is checked */
                    waiveActivationFee &&
                      !isActivationFeeAutomaticallyWaived && (
                        <Form.Item
                          label="Reason"
                          validateStatus={
                            waiveActivationFee &&
                            waiveActivationFeeReasonDirty &&
                            !waiveActivationFeeReasonValid
                              ? 'error'
                              : undefined
                          }
                          help={
                            waiveActivationFee &&
                            waiveActivationFeeReasonDirty &&
                            !waiveActivationFeeReasonValid
                              ? ERROR_MESSAGES.waiveActivationFeeReason
                              : undefined
                          }
                        >
                          <InputTextArea
                            id="waive-reason"
                            placeholder="Up to 500 characters"
                            value={waiveFeeReason}
                            onChange={e =>
                              setWaiveFeeReason(e.target.value.slice(0, 500))
                            }
                            onBlur={e =>
                              handleWaiveActivationFee(waiveActivationFee)
                            }
                            maxLength={500}
                            rows={2}
                          />
                        </Form.Item>
                      )
                  }
                </Col>
              )}
            </Row>
            {userState.supraSubscription?.type ===
              SupraSystemSubscriptionType.LIMITED &&
              userState.subRole && (
                <>
                  <Row>
                    <Col span={24}>
                      <Form.Item>
                        <Switch
                          data-cy="user-toggle-assign-lockboxes-release-shackle"
                          addonAfter="Assigned lockboxes and release shackle"
                          checked={
                            userState.supraSubscription
                              ?.assignLockboxesReleaseShackles
                          }
                          onChange={checked => {
                            setUserState(cur => ({
                              ...cur,
                              supraSubscription: {
                                ...cur.supraSubscription,
                                assignLockboxesReleaseShackles: checked,
                              },
                            }));
                          }}
                          disabled={areLimitedTogglesDisabled}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item>
                        <Switch
                          data-cy="user-toggle-scheduling-services"
                          addonAfter="Scheduling services"
                          checked={userState.features?.includes(
                            UserFeatures['Scheduling Services'],
                          )}
                          onChange={checked => {
                            handleSchedulingServiceToggle(checked);
                          }}
                          disabled={areLimitedTogglesDisabled}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}
          </>
        )}

      {!isAnsweringOrMlsStaff && !isProfile && (
        <>
          <Divider />
          <StyledTitle level={3}>Brokerage</StyledTitle>
          <BrokerageContainer>
            <BrokerageCircleLogo src={auth.org.logoUrl ?? undefined} />
            <Text>{auth.org.name}</Text>
          </BrokerageContainer>
          <Row gutter={24}>
            <Col span={hasFeature(Feature.TEAMS) ? 12 : 24}>
              <Form.Item
                label="Branch"
                validateStatus={
                  branchDirty && !branchValid ? 'error' : undefined
                }
                help={
                  branchDirty && !branchValid
                    ? ERROR_MESSAGES.branch
                    : undefined
                }
              >
                <Select
                  data-cy="user-select-branch"
                  showSearch
                  placeholder="Select"
                  disabled={isDeleted}
                  optionFilterProp="children"
                  value={branch}
                  onChange={b => {
                    setUserState(cur => ({
                      ...cur,
                      branch: b,
                      address: undefined,
                    }));
                    setUserStateDirty(cur => ({ ...cur, branch: true }));
                  }}
                  onBlur={() =>
                    setUserStateDirty(cur => ({ ...cur, branch: true }))
                  }
                  filterOption={(input, option) =>
                    (option?.children as unknown as string)
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {Object.entries(locations ?? []).map(([loc, { name }]) => (
                    <SelectOption value={loc} key={loc}>
                      {name}
                    </SelectOption>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          {!isDeleted &&
            role === UserRoles['Branch Admin'] &&
            branch !== undefined && (
              <Form.Item
                label="Branch Access"
                validateStatus={!branchAccessValid ? 'error' : undefined}
                help={
                  !branchAccessValid ? ERROR_MESSAGES.branchAccess : undefined
                }
              >
                <Select
                  data-cy="user-select-branch-access"
                  mode="multiple"
                  placeholder="Branches this user is authorized to access"
                  value={branchAccess}
                  onChange={val =>
                    setUserState(cur => ({ ...cur, branchAccess: val }))
                  }
                  filterOption={(input, option) => {
                    const lowerCasedInput = input.toLowerCase();
                    const lowerCasedOption = option?.children
                      ?.toString()
                      .toLowerCase();

                    return Boolean(lowerCasedOption?.includes(lowerCasedInput));
                  }}
                >
                  {Object.entries(locations ?? []).map(([loc, { name }]) => (
                    <SelectOption value={loc} key={loc}>
                      {name}
                    </SelectOption>
                  ))}
                </Select>
              </Form.Item>
            )}
          {Boolean(branch) && (
            <Form.Item
              label="Address"
              validateStatus={
                addressDirty && !addressValid ? 'error' : undefined
              }
              help={
                addressDirty && !addressValid
                  ? ERROR_MESSAGES.address
                  : undefined
              }
            >
              <Select
                data-cy="user-select-address"
                showSearch
                placeholder="Select"
                disabled={isDeleted}
                optionFilterProp="children"
                value={address}
                onChange={a => {
                  setUserState(cur => ({ ...cur, address: a }));
                  setUserStateDirty(cur => ({ ...cur, address: true }));
                }}
                onBlur={() =>
                  setUserStateDirty(cur => ({ ...cur, address: true }))
                }
              >
                {Object.entries(locations?.[branch ?? '']?.addresses ?? []).map(
                  ([key, addr]) => (
                    <SelectOption value={key} key={key}>
                      {addr.name || addr.address}
                      {addr.name && (
                        <div>
                          <DescriptionText>{addr.address}</DescriptionText>
                        </div>
                      )}
                    </SelectOption>
                  ),
                )}
              </Select>
            </Form.Item>
          )}
          {!isDeleted && hasFeature(Feature.TEAMS) && teams && (
            <Form.Item
              label="Team(s)"
              validateStatus={!teamAccessValid ? 'error' : undefined}
              help={!teamAccessValid ? ERROR_MESSAGES.teamAccess : undefined}
            >
              <Select
                data-cy="user-select-team-access"
                mode="multiple"
                placeholder="Select"
                value={teamAccess}
                onChange={val =>
                  setUserState(cur => ({ ...cur, teamAccess: val }))
                }
                filterOption={(input, option) => {
                  const lowerCasedInput = input.toLowerCase();
                  const lowerCasedOption = option?.children
                    ?.toString()
                    .toLowerCase();

                  return Boolean(lowerCasedOption?.includes(lowerCasedInput));
                }}
              >
                {teams.map(({ _id: id, name }, key) => (
                  <SelectOption value={id} key={key}>
                    {name}
                  </SelectOption>
                ))}
              </Select>
            </Form.Item>
          )}

          {auth.parentOrgType === 'mlsStaff' &&
            hasFeature(Feature.TEAMS) &&
            hasFeature(Feature.SUPRA_MVP) && (
              <Form.Item>
                <LinkButton
                  data-cy="user-button-add-team"
                  onClick={toggleAddTeamModal}
                >
                  <Icon
                    type="antdPlusCircleOutlined"
                    color="primary"
                    width={14}
                    height={14}
                  />
                  Add team
                </LinkButton>
              </Form.Item>
            )}
          {auth.parentOrgType === 'mlsStaff' &&
            hasFeature(Feature.SUPRA_MVP) &&
            hasFeature(Feature.TEAMS) && (
              <AddNewTeamModal
                visible={addTeamModalVisible}
                onSave={handleNewTeamSave}
                onCancel={toggleAddTeamModal}
              />
            )}
        </>
      )}

      {!isDeleted && !isAnsweringOrMlsStaff && !isProfile && (
        <>
          <Divider />

          <StyledTitle level={3}>Settings</StyledTitle>

          {shouldDisplayDeactivateUserLogin ? (
            <Form.Item>
              <Switch
                data-cy="user-toggle-deactivate-login"
                addonAfter="Deactivate user login"
                checked={disabled}
                onChange={checked =>
                  setUserState(cur => ({ ...cur, disabled: checked }))
                }
              />
            </Form.Item>
          ) : null}
          <Form.Item>
            <Switch
              data-cy="user-toggle-hide-profile"
              addonAfter="Hide profile to other agents in chat"
              checked={isAgentChatHidden}
              onChange={checked =>
                setUserState(cur => ({ ...cur, isAgentChatHidden: checked }))
              }
            />
          </Form.Item>
          {ROLES[role] >= ROLES.BRANCH_ADMIN && (
            <Form.Item>
              <Switch
                data-cy="user-toggle-include-frontDesk"
                addonAfter="Include in Front Desk admin branch chat"
                checked={isChatAdmin}
                onChange={checked =>
                  setUserState(cur => ({ ...cur, isChatAdmin: checked }))
                }
              />
            </Form.Item>
          )}
          <Form.Item>
            <Switch
              data-cy="user-toggle-inactive-chat"
              addonAfter="Set chat status to inactive"
              checked={isChatInactive}
              onChange={checked =>
                setUserState(cur => ({ ...cur, isChatInactive: checked }))
              }
            />
          </Form.Item>
        </>
      )}
    </Form>
  );
};

export default ProfileTab;

const StyledTitle = styled(Title)`
  /* !important to override antd default margin of 0.5em */
  margin-bottom: 24px !important;
`;

const FormItemLabelContainer = styled.div`
  padding-bottom: 8px;
`;

const MemberNumbersFormItem = styled(Form.Item)`
  &.ant-form-item-has-error {
    margin-bottom: 24px;
  }
  &:not(.ant-form-item-has-error) {
    // Delay the removal of bottom margin when input becomes valid, since
    // The hint text has a transition duration
    transition-property: margin-bottom;
    transition-delay: 0.5s;
  }
`;

const PrimaryMemberNumberRadio = styled(Radio)`
  margin-top: 8px;
`;

const RemoveMemberNumberButton = styled(LinkButton).attrs({ danger: true })`
  display: inline-flex;
  margin-top: 3px;
  position: absolute;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 8px;
`;

const BrokerageContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const BrokerageCircleLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 16px;

  filter: drop-shadow(0px 2px 8px rgba(0, 0, 0, 0.15));

  margin-right: 12px;
`;

const InputGroupSelect = styled(Select)<{ $width?: string }>`
  ${({ $width }) => ($width ? `width: ${$width};` : '')}
  /* Fix right side styling of Select element grouped compactly before an Input */
  margin-right: 0 !important;
  & > .ant-select {
    width: 100% !important;

    & > .ant-select-selector {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;

      .ant-select-selection-item .ant-typography-secondary {
        display: none;
      }
    }
  }
`;

const StyledInput = styled(Input)<{ $width?: string }>`
  ${({ $width }) => ($width ? `width: ${$width} !important;` : '')}

  //fix default antd style being overridden
  .ant-input:hover {
    border-color: ${theme.blue5};
  }
  :is(.ant-form-item-has-error *) .ant-input {
    border-color: ${theme.errorColor};
  }
  :is(.ant-input-group-compact > *) .ant-input {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
  }
`;

const InputGroupInput = styled(AntdInput)<{ $width?: string }>`
  ${({ $width }) => ($width ? `width: ${$width} !important;` : '')}
`;

// TODO: modularize board selection when it is used from multiple
// places of APP
const StyledBoardName = styled.div`
  width: auto;
  span {
    margin-left: 5px;
  }
`;

const MemberNumberAlert = styled(StyledAlert)`
  margin-bottom: 24px;
`;

const PasswordControlContainer = styled.div`
  display: flex;
  align-items: baseline;
`;

const Inline = styled.div`
  display: inline-block;
  margin-left: 8px;
`;
